import { createRouter, createWebHashHistory } from 'vue-router';

import { useDocumentListStore } from 'src/stores/documentList';
import { useFilterStore } from 'src/stores/filter';
import { useFilterListStore } from 'src/stores/filterList';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useIdoListStore } from 'src/stores/idoList';
import { useInboxStore } from 'src/stores/inbox';
import { useMailListStore } from 'src/stores/mailList';
import { useMessageListStore } from 'src/stores/messageList';
import { useNoteListStore } from 'src/stores/noteList';
import { usePhoneCallListStore } from 'src/stores/phoneCallList';
import { useTaskListStore } from 'src/stores/taskList';
import { useTrashListStore } from 'src/stores/trashList';

import { InstanceAuthenticationConfiguration } from 'src/models/authentication';
import { MODULE } from 'src/models/genericActivityList';

import { serializeActivityListDateRouteParam } from 'src/router/activityListDateRouteParam';
import { resolveAuthenticationRedirect } from 'src/router/authenticationRedirect';
import { createNormalizedActivityListRoute } from 'src/router/normalizeActivityListRoute';
import routes from 'src/router/routes';

import {
	ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY,
	INSTANCE_AUTHENTICATION_CONFIGURATION_KEY,
} from 'src/services/authentication';

import { formatDate } from 'src/util/date';

export default function createAppRouter() {
	const Router = createRouter({
		history: createWebHashHistory(),
		routes,

		scrollBehavior(to, from, savedPosition) {
			if (
				from.meta.type == 'activityList' &&
				to.meta?.type === 'activityList' &&
				from.meta.mainMenuItemName !== to.meta?.mainMenuItemName
			) {
				const genericActivityListStore = useGenericActivityListStore();
				void genericActivityListStore.clearTheCurrentlyActiveDesiredGroupingSeparatorHeader({
					genericListIdentifier: to.name as string,
				});
			}

			if (savedPosition) {
				return savedPosition;
			} else {
				return { left: 0, top: 0 };
			}
		},
	});

	Router.beforeEach((to, from, next) => {
		const publicPages = ['/login', '/versionMismatch'];
		const authRequired =
			!publicPages.includes(to.path) && to.name !== 'OAuthConsentPage' && to.name !== 'OAuthAuthorizePage';
		let selectedInstanceAuthentication = undefined;
		let allInstanceConfigurations: InstanceAuthenticationConfiguration[] = [];
		const selectedInstanceAuthenticationName = localStorage.getItem(ACTIVE_INSTANCE_AUTHENTICATION_NAME_KEY);
		const allInstanceConfigurationsRaw = localStorage.getItem(INSTANCE_AUTHENTICATION_CONFIGURATION_KEY) as string;
		if (allInstanceConfigurationsRaw !== null) {
			try {
				const parsedConfigurations = JSON.parse(
					allInstanceConfigurationsRaw
				) as InstanceAuthenticationConfiguration[];
				allInstanceConfigurations = Array.isArray(parsedConfigurations) ? parsedConfigurations : [];
				selectedInstanceAuthentication = allInstanceConfigurations.find(
					(instanceConfiguration) =>
						instanceConfiguration.name.toLowerCase() === selectedInstanceAuthenticationName?.toLowerCase()
				);
			} catch {
				/* empty */
			}
		}

		to.meta.previousRouteName = from.name;

		if (Object.prototype.hasOwnProperty.call(from.params, 'activityListLocation')) {
			to.meta.activityListLocation = from.params.activityListLocation;
		}

		const authenticationRedirect = resolveAuthenticationRedirect({
			authRequired,
			routeName: to.name,
			selectedInstanceAuthentication,
		});

		if (authenticationRedirect !== null) {
			return next(authenticationRedirect);
		}

		if (
			Object.values(MODULE).includes(String(to.name) as MODULE) &&
			to.name !== MODULE.IDO_LIST &&
			to.name !== MODULE.IDO_HISTORY &&
			to.name !== MODULE.AGENDA_LIST
		) {
			const documentListStore = useDocumentListStore();
			const filterListStore = useFilterListStore();
			const genericActivityListStore = useGenericActivityListStore();
			const inboxStore = useInboxStore();
			const mailListStore = useMailListStore();
			const messageListStore = useMessageListStore();
			const noteListStore = useNoteListStore();
			const phoneCallListStore = usePhoneCallListStore();
			const taskListStore = useTaskListStore();
			const trashListStore = useTrashListStore();

			const today = new Date().toISOString().split('T')[0];
			let desiredModule = '';
			let desiredDate = formatDate(new Date(), 'YYYY-MM-DD');
			let desiredSource = 'main';
			let desiredView = 'automatic';

			switch (to.name) {
				case MODULE.DOCUMENT_LIST:
					desiredModule = MODULE.DOCUMENT_LIST;
					desiredDate = serializeActivityListDateRouteParam(documentListStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.DOCUMENT_LIST)).toLowerCase();

					if (documentListStore.today !== today) {
						documentListStore.setToday(today);
						documentListStore.setDateFilterValue(today);
						desiredDate = today;
					}

					break;

				case MODULE.FILTER_LIST:
					desiredModule = MODULE.FILTER_LIST;
					desiredDate = serializeActivityListDateRouteParam(filterListStore.dateFilter);
					desiredSource = 'main';
					desiredView = 'all';
					break;

				case MODULE.INBOX:
					desiredModule = MODULE.INBOX;
					desiredDate = serializeActivityListDateRouteParam(inboxStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.INBOX)).toLowerCase();
					break;

				case MODULE.MAIL_LIST:
					desiredModule = MODULE.MAIL_LIST;
					desiredDate = serializeActivityListDateRouteParam(mailListStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.MAIL_LIST)).toLowerCase();

					if (mailListStore.today !== today) {
						mailListStore.setToday(today);
						mailListStore.setDateFilterValue(today);
						desiredDate = today;
					}

					break;

				case MODULE.MESSAGE_LIST:
					desiredModule = MODULE.MESSAGE_LIST;
					desiredDate = serializeActivityListDateRouteParam(messageListStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.MESSAGE_LIST)).toLowerCase();

					if (messageListStore.today !== today) {
						messageListStore.setToday(today);
						messageListStore.setDateFilterValue(today);
						desiredDate = today;
					}

					break;

				case MODULE.NOTE_LIST:
					desiredModule = MODULE.NOTE_LIST;
					desiredDate = serializeActivityListDateRouteParam(noteListStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.NOTE_LIST)).toLowerCase();

					if (noteListStore.today !== today) {
						noteListStore.setToday(today);
						noteListStore.setDateFilterValue(today);
						desiredDate = today;
					}

					break;

				case MODULE.PHONE_CALL_LIST:
					desiredModule = MODULE.PHONE_CALL_LIST;
					desiredDate = serializeActivityListDateRouteParam(phoneCallListStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.PHONE_CALL_LIST)).toLowerCase();

					if (phoneCallListStore.today !== today) {
						phoneCallListStore.setToday(today);
						phoneCallListStore.setDateFilterValue(today);
						desiredDate = today;
					}

					break;

				case MODULE.TASK_LIST:
					desiredModule = MODULE.TASK_LIST;
					desiredDate = serializeActivityListDateRouteParam(taskListStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.TASK_LIST)).toLowerCase();

					if (taskListStore.today !== today) {
						taskListStore.setToday(today);
						taskListStore.setDateFilterValue(today);
						desiredDate = today;
					}

					break;

				case MODULE.TRASH_LIST:
					desiredModule = MODULE.TRASH_LIST;
					desiredDate = serializeActivityListDateRouteParam(trashListStore.dateFilter);
					desiredSource = 'main';
					desiredView = String(genericActivityListStore.getActiveView(MODULE.TRASH_LIST)).toLowerCase();

					if (trashListStore.today !== today) {
						trashListStore.setToday(today);
						trashListStore.setDateFilterValue(today);
						desiredDate = today;
					}

					break;

				default:
					break;
			}

			const {
				date: curDate,
				source: curSource,
				view: curView,
			} = to.params as {
				date?: string;
				source?: string;
				view?: string;
			};
			const normalizedCurrentView = typeof curView === 'string' ? curView.toLowerCase() : null;
			const isValidIncomingView =
				normalizedCurrentView === 'all' || normalizedCurrentView === 'mine' || normalizedCurrentView === 'open';
			// `?list=` markiert einen isolierten Listen-Tab (saved-lists-as-isolated-tabs) — dessen In-Tab-
			// View-Wechsel (updateCurrentTabRoute) pushen weiterhin source:'main' (kein Menübaum-Öffnen-Event),
			// tragen aber bereits die korrekten Instanz-Params. Ohne diesen Zweig überschreibt der Guard sie
			// mit den Modul-Defaults, weil er nur source==='folderExplorer' kennt (Defect C).
			const currentListQueryValue = Array.isArray(to.query.list) ? to.query.list[0] : to.query.list;
			const hasNonEmptySharedQueryListQuery =
				typeof currentListQueryValue === 'string' && currentListQueryValue.trim() !== '';
			const shouldPreferFolderExplorerRouteParams =
				(curSource === 'folderExplorer' && typeof curDate === 'string' && isValidIncomingView) ||
				(hasNonEmptySharedQueryListQuery && isValidIncomingView);

			if (shouldPreferFolderExplorerRouteParams) {
				desiredDate = curDate;
				desiredSource = curSource;
				desiredView = normalizedCurrentView;
			}

			const normalizedActivityListRoute = createNormalizedActivityListRoute({
				currentHash: to.hash,
				currentName: to.name,
				currentParams: {
					date: curDate,
					source: curSource,
					view: curView,
				},
				currentQuery: to.query,
				desiredDate: desiredDate,
				desiredModule: desiredModule,
				desiredSource: desiredSource,
				desiredView: desiredView,
			});

			if (normalizedActivityListRoute !== null) {
				void Router.replace(normalizedActivityListRoute);
				next();
			} else {
				next();
			}
		} else {
			next();
		}
	});

	Router.afterEach((to) => {
		try {
			const filterStore = useFilterStore();
			const idoListStore = useIdoListStore();
			const isToActivityList = to.meta?.type === 'activityList';

			if (!isToActivityList && to.name) {
				void filterStore.setDesiredSearchFieldStateVisible({
					desiredSearchFieldStateVisible: false,
					module: String(to.name),
				});

				// Intentionally NOT resetting the flag for to.meta.mainMenuItemName:
				// opening a detail view must not collapse the originating list's
				// search zone — its open/closed state is stateful across navigation.
			}

			if (isToActivityList && to.name) {
				let hasSearchTerm = false;
				if (to.name === 'records') {
					const term = idoListStore.globalSearchTerm;
					hasSearchTerm = typeof term === 'string' && term.trim() !== '';
				} else {
					const term = filterStore.searchTerm(String(to.name));
					hasSearchTerm = typeof term === 'string' && term.trim() !== '';
				}

				if (hasSearchTerm) {
					void filterStore.setDesiredSearchFieldStateVisible({
						desiredSearchFieldStateVisible: true,
						module: String(to.name),
					});

					setTimeout(() => {
						try {
							window.dispatchEvent(new Event('resize'));
						} catch {
							/* no-op */
						}
					}, 0);
				}
			}
		} catch {
			/* no-op */
		}
	});

	return Router;
}
